import { useCallback, useState } from 'react';
import { CLEAN_GENERATION, isDirty, markEdited, markSaved } from './dirty-generation';

/**
 * React state for one document's dirty generation (PROD-2700) — see `lib/dirty-generation.ts`.
 *
 * `generation` is read in the SAME render as the content a save snapshots, so the pair a save
 * records is consistent. `markSavedThrough` applies against the LATEST state (functional update), so
 * an edit made while the save was in flight is still counted when the save succeeds.
 */
export function useDirtyGeneration() {
  const [state, setState] = useState(CLEAN_GENERATION);
  /** An edit happened: the document is dirty until a save carrying this edit succeeds. */
  const markDirty = useCallback(() => setState(markEdited), []);
  /** A save that snapshotted `generation` succeeded. Clears dirty only if nothing newer was edited. */
  const markSavedThrough = useCallback(
    (generation: number) => setState((prev) => markSaved(prev, generation)),
    [],
  );
  return { dirty: isDirty(state), generation: state.edited, markDirty, markSavedThrough };
}
